/**
 * Click-to-move input adapter — GH#8 Phase 1 J1 completion.
 *
 * Pure routing helpers that translate a canvas-click + modifier state +
 * the local player's position into one of:
 *
 *   - `move`    — orthogonally-adjacent left-click; dispatch a `MoveIntent`
 *                 with the derived `CardinalDirection`.
 *   - `inspect` — modifier-click, click on self-tile, no self-player yet,
 *                 or any non-adjacent left-click. Per Q-GH8-5, falling
 *                 back to inspect lets the operator probe distant tiles
 *                 and use Shift-click to inspect an adjacent tile without
 *                 walking onto it.
 *
 * Server-authoritative per I6 — the move path dispatches the intent and
 * waits for the resulting `PlayerMoved` event to update the avatar's
 * rendered position. No optimistic update; the renderer still owns the
 * 1-tick interpolation animation it had before this directive.
 *
 * Coord convention per WORLD_MODEL §2: integer `(x, y)` tile space with
 * `+x = East`, `+y = South`. Cardinal mapping below uses that.
 */

import type { CardinalDirection, TileCoord } from '../wire';

/** Discriminated outcome of `routeTileClick`. */
export type ClickAction =
  | { kind: 'move'; direction: CardinalDirection }
  | { kind: 'inspect' };

/**
 * Pure: derive `CardinalDirection` from a `selfTile → clickTile` delta.
 * Returns `null` for diagonals, non-adjacent clicks, or clicks on the
 * self-tile itself. Manhattan distance must be exactly 1.
 */
export function cardinalFromDelta(
  selfTile: TileCoord,
  clickTile: TileCoord,
): CardinalDirection | null {
  const dx = clickTile[0] - selfTile[0];
  const dy = clickTile[1] - selfTile[1];
  if (Math.abs(dx) + Math.abs(dy) !== 1) return null;
  if (dx === 1) return 'East';
  if (dx === -1) return 'West';
  if (dy === 1) return 'South';
  return 'North';
}

/**
 * Pure click router. The caller wires the chosen `ClickAction` to its
 * concrete handlers (live-wire send for `move`, inspect-store opener
 * for `inspect`).
 *
 * Disambiguation:
 *   - `shiftKey` modifier → inspect (lets operator inspect adjacent
 *     tile without moving onto it)
 *   - `selfTile === null` → inspect (no local player → can't move)
 *   - click on self-tile → inspect
 *   - cardinally-adjacent → move
 *   - everything else → inspect (per Q-GH8-5)
 */
export function routeTileClick(args: {
  selfTile: TileCoord | null;
  clickTile: TileCoord;
  shiftKey: boolean;
}): ClickAction {
  if (args.shiftKey || args.selfTile === null) return { kind: 'inspect' };
  if (
    args.clickTile[0] === args.selfTile[0] &&
    args.clickTile[1] === args.selfTile[1]
  ) {
    return { kind: 'inspect' };
  }
  const direction = cardinalFromDelta(args.selfTile, args.clickTile);
  return direction === null
    ? { kind: 'inspect' }
    : { kind: 'move', direction };
}
